import { type ReactElement, type ReactNode, useCallback, useEffect, useId, useRef, useState } from 'react';
import { UiIcon } from './icons';
import { useEscape, useLayerStack, useModalFocus } from './overlays';
import { useConsoleTheme } from '../theme-preference';

/**
 * The responsive console shell (W8-MERCHANT-KITCHEN, D447, ADR-0077; finding M-15; widened by V-LIB-SHELL, D509) — the
 * Admin's D319 "shell 1a" (a navy sidebar, a top bar, white content) as ONE shared component, so a console no longer
 * hand-builds it with a fixed 248 px sidebar that ate a phone or a small kitchen tablet. Every console (PDV, Merchant,
 * Fleet, Agent desk, Admin) composes this; none re-declares the `<aside>`/`<header>`/`<main>`.
 *
 * Wide screens: the sidebar sits beside the content. At `max-width: 900px` (theme.css `.vello-console-shell`) the sidebar
 * becomes an off-canvas drawer: a 48 px menu button opens it over a scrim; the scrim, `Escape`, a destination picked
 * inside it (`close` is handed to `sidebar`) or the button close it; focus moves into the drawer when it opens and back
 * to the button when it closes (the dialog pattern of `useModalFocus`), and the content padding shrinks to the 16 px
 * gutter. The slide keeps playing under reduced motion, as in the design (only the FX effect classes are stilled).
 *
 * Frame (D547, owner decision of the design-fidelity wave; Admin Web A08): the shell fills the viewport — the sidebar and
 * the top bar are pinned and the page scrolls INSIDE `main` (page padding 24px 32px 28px, a column gap of 18) — and the
 * page is WIDE by default: the top bar's row and the page run edge to edge, so a big monitor is used, not a 1200px column
 * in a corner. `contentWidth="boxed"` puts both back in one `--content-max` frame (a reading page, a form).
 *
 * The top bar has named slots, in this order: menu button (narrow only) · `breadcrumb` · space · `search` · `scope` ·
 * `actions` (· legacy `topbar`). Use {@link Breadcrumb} for the crumbs, {@link ScopeSwitcher} for the scope, the
 * Admin's ⌘K trigger (`vello-palette-trigger`) for `search`, a bell/`IconButton` for `actions`. `topbar` remains for a
 * console that has not moved to the slots: it is rendered after them, unchanged.
 *
 * `sidebar` renders the destinations — see {@link SidebarNav} — given `close` (call it after a navigation) and a context:
 * `collapsed` is true when the icon rail is on (`collapsed` prop, wide screens only; a phone drawer is always full).
 * Give `onToggleCollapsed` (+ the two labels) and the shell draws the edge toggle the Admin has.
 * `footer` is a muted line (a build/version, a one-line note) at the end of the page, never a bar over the content; `devInfo` is diagnostic text (the API base URL) that is
 * rendered ONLY in a development build — a production operator never sees it (audit consoles F11/F13).
 * Every label is the caller's (translated) text.
 */
export interface ConsoleShellSidebarContext {
  /** True while the icon rail is shown (collapsed AND wide screen). */
  readonly collapsed: boolean;
  /** True below the drawer breakpoint. */
  readonly narrow: boolean;
}

export interface ConsoleShellProps {
  readonly sidebar: (close: () => void, context: ConsoleShellSidebarContext) => ReactNode;
  /** The sidebar's accessible name ("Store sections"). */
  readonly sidebarLabel: string;
  /** Legacy free-form top-bar content, rendered after the named slots. Prefer the slots. */
  readonly topbar?: ReactNode;
  /** Left of the top bar: the crumbs ({@link Breadcrumb}). */
  readonly breadcrumb?: ReactNode;
  /** The global search / command-palette trigger. */
  readonly search?: ReactNode;
  /** The scope pill ({@link ScopeSwitcher}) — one pattern for every console. */
  readonly scope?: ReactNode;
  /** Right of the bar: notifications, presence, help… */
  readonly actions?: ReactNode;
  readonly footer?: ReactNode;
  /** Diagnostic line, rendered only when {@link isDevBuild}. */
  readonly devInfo?: ReactNode;
  /** The icon rail (wide screens). */
  readonly collapsed?: boolean;
  readonly onToggleCollapsed?: () => void;
  /** The edge toggle's names ("Collapse menu" / "Expand menu"); required with `onToggleCollapsed`. */
  readonly collapseLabel?: string;
  readonly expandLabel?: string;
  /** `full` (default): edge to edge, like the design; `boxed`: the top bar's row and the page share one `--content-max` frame. */
  readonly contentWidth?: 'full' | 'boxed';
  /** The menu button's accessible name ("Open menu"). */
  readonly menuLabel: string;
  /** The scrim's accessible name ("Close menu"). */
  readonly closeMenuLabel: string;
  readonly children: ReactNode;
}

/** The width at and below which the sidebar is a drawer (kept equal to the theme.css media query). */
export const CONSOLE_SHELL_DRAWER_MAX_WIDTH = 900;

function isNarrow(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia(`(max-width: ${CONSOLE_SHELL_DRAWER_MAX_WIDTH}px)`).matches;
}

/**
 * Whether this is a development build (Vite's `import.meta.env.DEV`). Read at call time so a test can stub it. The
 * shell renders `devInfo` only when this is true.
 */
export function isDevBuild(): boolean {
  return import.meta.env?.DEV === true;
}

export interface BreadcrumbItem {
  readonly label: string;
  /** Makes the crumb a button (a record's list). The last item is the current page and never navigates. */
  readonly onClick?: () => void;
}

/** The top bar's crumbs ("Store > Orders"): chevron-separated, the last one is the current page (`aria-current`). */
export function Breadcrumb({ items, ariaLabel }: { items: readonly BreadcrumbItem[]; ariaLabel: string }): ReactElement {
  return (
    <nav className="vello-breadcrumb" aria-label={ariaLabel}>
      {items.map((item, i) => {
        const last = i === items.length - 1;
        return (
          <span key={`${i}-${item.label}`} className="vello-breadcrumb__item">
            {i > 0 && (
              <span className="vello-breadcrumb__sep" aria-hidden="true">
                <UiIcon name="chevronRight" size={13} />
              </span>
            )}
            {!last && item.onClick !== undefined ? (
              <button type="button" className="vello-crumb vello-crumb--link" onClick={item.onClick}>
                {item.label}
              </button>
            ) : (
              <span className={`vello-crumb${last ? ' vello-crumb--current' : ''}`} aria-current={last ? 'page' : undefined}>
                {item.label}
              </span>
            )}
          </span>
        );
      })}
    </nav>
  );
}

export function ConsoleShell({ sidebar, sidebarLabel, topbar, breadcrumb, search, scope, actions, footer, devInfo, collapsed = false, onToggleCollapsed, collapseLabel, expandLabel, contentWidth = 'full', menuLabel, closeMenuLabel, children }: ConsoleShellProps): ReactElement {
  const [open, setOpen] = useState(false);
  const [narrow, setNarrow] = useState<boolean>(isNarrow);
  const drawerId = useId();
  const asideRef = useRef<HTMLElement | null>(null);
  const close = useCallback((): void => setOpen(false), []);
  const drawerOpen = open && narrow;
  const rail = collapsed && !narrow;
  const isTop = useLayerStack(drawerOpen);
  useEscape(drawerOpen, close, isTop);
  useModalFocus(asideRef, drawerOpen, drawerOpen, isTop);
  // D798: the operator's theme applies while the console is on screen (never to a sign-in screen).
  useConsoleTheme();

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia(`(max-width: ${CONSOLE_SHELL_DRAWER_MAX_WIDTH}px)`);
    const onChange = (): void => {
      setNarrow(mq.matches);
      if (!mq.matches) setOpen(false);
    };
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);

  const showDev = devInfo !== undefined && isDevBuild();
  const hasFoot = footer !== undefined || showDev;

  return (
    <div className="vello-console-shell" data-drawer={drawerOpen ? 'open' : 'closed'} data-collapsed={rail ? 'true' : undefined} data-width={contentWidth}>
      <aside
        ref={asideRef}
        id={drawerId}
        className="vello-sidebar vello-console-shell__sidebar"
        aria-label={sidebarLabel}
        tabIndex={-1}
        {...(narrow && !drawerOpen ? { inert: true, 'aria-hidden': true } : {})}
        {...(drawerOpen ? { 'data-vello-layer': 'open', role: 'dialog', 'aria-modal': true } : {})}
      >
        {onToggleCollapsed !== undefined && !narrow && (
          <button type="button" className="vello-sidebar__toggle" onClick={onToggleCollapsed} aria-label={rail ? expandLabel : collapseLabel} title={rail ? expandLabel : collapseLabel} aria-expanded={!rail}>
            <UiIcon name={rail ? 'chevronRight' : 'chevronLeft'} size={13} />
          </button>
        )}
        {sidebar(close, { collapsed: rail, narrow })}
      </aside>
      {drawerOpen && <button type="button" className="vello-console-shell__scrim" aria-label={closeMenuLabel} onClick={close} />}
      <div className="vello-console-shell__content">
        <header className="vello-topbar vello-console-shell__topbar">
          <div className="vello-console-shell__bar">
            <button type="button" className="vello-console-shell__menu" aria-label={menuLabel} aria-controls={drawerId} aria-expanded={drawerOpen} onClick={() => setOpen((o) => !o)}>
              <UiIcon name="panelLeft" size={18} />
            </button>
            {breadcrumb}
            {topbar === undefined && <span className="vello-console-shell__spacer" />}
            {search}
            {scope}
            {actions}
            {topbar}
          </div>
        </header>
        <main className="vello-console-shell__main">
          <div className="vello-console-shell__page">{children}</div>
          {/* D854 (L3-006): the footer line ends the scrolling page; a pinned bar cut through the last card and the button rows on a phone. */}
          {hasFoot && (
            <footer className="vello-console-shell__footer">
              {footer}
              {showDev && <div className="vello-console-shell__devinfo">{devInfo}</div>}
            </footer>
          )}
        </main>
      </div>
    </div>
  );
}
